import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Phone, PhoneOff, Mic, MicOff } from 'lucide-react';
import useVoipMesh from '../../hooks/useVoipMesh';

const BOARD_SIZE = 15;
// Color orientation follows the provided C reference board:
// top-left green, top-right yellow, bottom-right blue, bottom-left red.
const PLAYER_COLORS = ['green', 'yellow', 'blue', 'red'];
const COLOR_HEX = {
  red: '#ef4444',
  green: '#22c55e',
  yellow: '#facc15',
  blue: '#3b82f6'
};

const PATH = [
  [6, 1], [6, 2], [6, 3], [6, 4], [6, 5], [5, 6], [4, 6], [3, 6], [2, 6], [1, 6], [0, 6], [0, 7], [0, 8],
  [1, 8], [2, 8], [3, 8], [4, 8], [5, 8], [6, 9], [6, 10], [6, 11], [6, 12], [6, 13], [6, 14], [7, 14], [8, 14],
  [8, 13], [8, 12], [8, 11], [8, 10], [8, 9], [9, 8], [10, 8], [11, 8], [12, 8], [13, 8], [14, 8], [14, 7], [14, 6],
  [13, 6], [12, 6], [11, 6], [10, 6], [9, 6], [8, 5], [8, 4], [8, 3], [8, 2], [8, 1], [8, 0], [7, 0], [6, 0]
];

const SAFE_INDEXES = new Set([0, 8, 13, 21, 26, 34, 39, 47]);
const OFFSETS = { green: 0, yellow: 13, blue: 26, red: 39 };
const START_INDEX_BY_COLOR = { green: 0, yellow: 13, blue: 26, red: 39 };

const HOME_LANE = {
  green: [[7, 1], [7, 2], [7, 3], [7, 4]],
  yellow: [[1, 7], [2, 7], [3, 7], [4, 7]],
  blue: [[7, 13], [7, 12], [7, 11], [7, 10]],
  red: [[13, 7], [12, 7], [11, 7], [10, 7]]
};

const BASE_SLOTS = {
  green: [[1, 1], [1, 3], [3, 1], [3, 3]],
  yellow: [[1, 11], [1, 13], [3, 11], [3, 13]],
  red: [[11, 1], [11, 3], [13, 1], [13, 3]],
  blue: [[11, 11], [11, 13], [13, 11], [13, 13]]
};

const CELL_STACK_OFFSETS = [
  [-8, -8],
  [8, -8],
  [-8, 8],
  [8, 8]
];
const EDGE_STACK_INSET = 6;

const keyOf = (r, c) => `${r}-${c}`;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const parseInviteToken = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const fromPath = raw.match(/\/ludo\/join\/([A-Za-z0-9_-]+)/i);
  if (fromPath?.[1]) return fromPath[1];

  const fromJoinText = raw.match(/\bjoin\s+([A-Za-z0-9_-]+)/i);
  if (fromJoinText?.[1]) return fromJoinText[1];

  const fromTokenLabel = raw.match(/\btoken[:\s]+([A-Za-z0-9_-]+)/i);
  if (fromTokenLabel?.[1]) return fromTokenLabel[1];

  const chunks = raw.split(/[\s/]+/).filter(Boolean);
  const candidate = chunks[chunks.length - 1] || '';
  if (/^[A-Za-z0-9_-]{6,}$/.test(candidate)) return candidate;

  return raw;
};

const DiceFace = ({ value = 1, rolling = false }) => {
  const normalized = Math.max(1, Math.min(6, Number(value) || 1));
  const pips = {
    1: [[1, 1]],
    2: [[0, 0], [2, 2]],
    3: [[0, 0], [1, 1], [2, 2]],
    4: [[0, 0], [0, 2], [2, 0], [2, 2]],
    5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]],
    6: [[0, 0], [1, 0], [2, 0], [0, 2], [1, 2], [2, 2]]
  };

  return (
    <div
      className={`relative h-11 w-11 rounded-lg border border-gray-400 shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),0_2px_6px_rgba(0,0,0,0.25)] bg-gradient-to-br from-white via-gray-50 to-gray-200 transition-transform ${rolling ? 'animate-[diceRoll_0.7s_ease-in-out]' : ''}`}
      style={{ transformStyle: 'preserve-3d' }}
    >
      <div className="absolute inset-1 grid grid-cols-3 grid-rows-3">
        {pips[normalized].map(([r, c], idx) => (
          <span
            key={`${r}-${c}-${idx}`}
            className="h-2 w-2 self-center justify-self-center rounded-full bg-gray-800 shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)]"
            style={{ gridRow: r + 1, gridColumn: c + 1 }}
          />
        ))}
      </div>
    </div>
  );
};

const PinToken = ({ color, isActive }) => {
  const base = COLOR_HEX[color] || '#3b82f6';
  return (
    <div className={`relative h-5 w-4 ${isActive ? 'animate-[pinPulse_1.2s_ease-in-out_infinite]' : ''}`}>
      <div
        className="absolute left-1/2 top-0 h-3.5 w-3.5 -translate-x-1/2 rounded-full border border-white shadow"
        style={{ background: `radial-gradient(circle at 35% 35%, #fff 0%, ${base} 45%, ${base} 100%)` }}
      />
      <div
        className="absolute left-1/2 top-[10px] h-0 w-0 -translate-x-1/2 border-l-[4px] border-r-[4px] border-t-[7px] border-l-transparent border-r-transparent"
        style={{ borderTopColor: base }}
      />
      <div className="absolute bottom-0 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-black/20" />
    </div>
  );
};

const getSafeTokenOffset = (token, stackIndex) => {
  let [dx, dy] = CELL_STACK_OFFSETS[stackIndex % CELL_STACK_OFFSETS.length];

  // Keep stacked tokens inside edge cells so they remain clickable.
  if (token.r <= 1 && dy < 0) dy = EDGE_STACK_INSET;
  if (token.r >= BOARD_SIZE - 2 && dy > 0) dy = -EDGE_STACK_INSET;
  if (token.c <= 1 && dx < 0) dx = EDGE_STACK_INSET;
  if (token.c >= BOARD_SIZE - 2 && dx > 0) dx = -EDGE_STACK_INSET;

  return [dx, dy];
};

const LudoTab = ({ user, ws }) => {
  const [wallet, setWallet] = useState({ coins: 0, can_claim: false, daily_reward: 100 });
  const [sessions, setSessions] = useState([]);
  const [mode, setMode] = useState(2);
  const [joinToken, setJoinToken] = useState('');
  const [activeSession, setActiveSession] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [diceDisplay, setDiceDisplay] = useState(1);
  const [diceRolling, setDiceRolling] = useState(false);
  const [movableTokens, setMovableTokens] = useState([]);
  const [ludoCallInfo, setLudoCallInfo] = useState(null);
  const [ludoInCall, setLudoInCall] = useState(false);
  const [ludoMuted, setLudoMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [playerNamesById, setPlayerNamesById] = useState({});
  const [lastRollInfo, setLastRollInfo] = useState(null);
  const lastRollNoticeKeyRef = useRef('');

  const loadData = async () => {
    try {
      const [walletRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/coins/wallet`),
        axios.get(`${API}/ludo/sessions`)
      ]);

      setWallet(walletRes.data || { coins: 0, can_claim: false, daily_reward: 100 });
      const nextSessions = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
      setSessions(nextSessions);

      const mySession = nextSessions.find((s) => Array.isArray(s.players) && s.players.includes(user?.id));
      setActiveSession(mySession || null);

      if (mySession?.status === 'playing') {
        try {
          const stateRes = await axios.get(`${API}/ludo/sessions/${mySession.id}/state`);
          const nextGameState = stateRes.data?.game_state || null;
          setGameState(nextGameState);
          const liveDice = stateRes.data?.game_state?.dice_value;
          if (liveDice >= 1 && liveDice <= 6) {
            setDiceDisplay(liveDice);
            if (Array.isArray(nextGameState?.movable_tokens)) {
              setMovableTokens(nextGameState.movable_tokens);
            }
          } else {
            setMovableTokens([]);
          }
        } catch {
          setGameState(null);
          setMovableTokens([]);
        }
        try {
          const callRes = await axios.get(`${API}/voip/active`, {
            params: { scope: 'ludo', target_id: mySession.id }
          });
          if (callRes.data?.active && callRes.data?.call) {
            setLudoCallInfo(callRes.data.call);
            setLudoInCall(Boolean(callRes.data.is_participant));
          } else {
            setLudoCallInfo(null);
            setLudoInCall(false);
            setLudoMuted(false);
          }
        } catch {
          setLudoCallInfo(null);
          setLudoInCall(false);
          setLudoMuted(false);
        }
      } else {
        setGameState(null);
        setMovableTokens([]);
        setLudoCallInfo(null);
        setLudoInCall(false);
        setLudoMuted(false);
      }
    } catch {
      setSessions([]);
      setGameState(null);
      setMovableTokens([]);
      setLudoCallInfo(null);
      setLudoInCall(false);
      setLudoMuted(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'guest') return;
    loadData();
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'guest') return;
    const timer = setInterval(loadData, 3000);
    return () => clearInterval(timer);
  }, [user?.role]);

  const playerIds = activeSession?.players || [];

  const getPlayerLabel = useCallback((playerId, seatIndex) => {
    if (!playerId) return 'Waiting...';
    if (playerId === user?.id) return user?.display_name || user?.username || 'You';
    return playerNamesById[playerId] || `Player ${seatIndex + 1}`;
  }, [playerNamesById, user?.id, user?.display_name, user?.username]);

  useEffect(() => {
    if (!Array.isArray(playerIds) || playerIds.length === 0) return;

    const missingIds = playerIds.filter((pid) => pid && !playerNamesById[pid] && pid !== user?.id);
    if (missingIds.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const responses = await Promise.all(
          missingIds.map((pid) => axios.get(`${API}/users/${pid}`).catch(() => null))
        );

        if (cancelled) return;
        const updates = {};
        responses.forEach((res, idx) => {
          const pid = missingIds[idx];
          const data = res?.data || {};
          const label = (data.display_name || data.username || '').trim();
          if (label) updates[pid] = label;
        });

        if (Object.keys(updates).length > 0) {
          setPlayerNamesById((prev) => ({ ...prev, ...updates }));
        }
      } catch {
        // Best-effort UI enrichment; keep fallbacks if profile lookup fails.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [playerIds, playerNamesById, user?.id]);

  const houseLabels = useMemo(() => {
    return PLAYER_COLORS.map((color, seatIndex) => {
      const playerId = playerIds[seatIndex];
      return {
        color,
        seatIndex,
        playerId,
        label: getPlayerLabel(playerId, seatIndex)
      };
    });
  }, [playerIds, getPlayerLabel]);

  const claimDaily = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/coins/claim-daily`);
      toast.success('Daily coins claimed');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to claim reward');
    } finally {
      setLoading(false);
    }
  };

  const createSession = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/ludo/sessions`, { mode });
      const token = res.data?.invite_token || '';
      const link = res.data?.invite_link || (token ? `/ludo/join/${token}` : '');
      toast.success(token ? `Session created. Invite token: ${token}` : 'Session created');
      if (token) setJoinToken(token);
      if (link) toast.info(`Share link: ${link}`);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to create game');
    } finally {
      setLoading(false);
    }
  };

  const joinByToken = async (token) => {
    const inviteToken = parseInviteToken(token || joinToken);
    if (!inviteToken) {
      toast.error('Enter invite token');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/ludo/join/${inviteToken}`);
      toast.success('Joined ludo session');
      setJoinToken('');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to join game');
    } finally {
      setLoading(false);
    }
  };

  const joinBySessionId = async (sessionId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/ludo/sessions/${sessionId}/join`);
      toast.success('Joined ludo session');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to join game');
    } finally {
      setLoading(false);
    }
  };

  const startSession = async (sessionId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/ludo/sessions/${sessionId}/start`);
      toast.success('Game started');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to start game');
    } finally {
      setLoading(false);
    }
  };

  const cancelSession = async (sessionId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/ludo/sessions/${sessionId}/cancel`);
      toast.success('Game cancelled');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to cancel game');
    } finally {
      setLoading(false);
    }
  };

  const leaveSession = async (sessionId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/ludo/sessions/${sessionId}/leave`);
      toast.success('Left ludo session');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to leave game');
    } finally {
      setLoading(false);
    }
  };

  const rollDice = async () => {
    if (!activeSession?.id) return;
    if (diceRolling) return;
    try {
      setLoading(true);
      setDiceRolling(true);
      for (let i = 0; i < 8; i += 1) {
        setDiceDisplay((i % 6) + 1);
        // eslint-disable-next-line no-await-in-loop
        await sleep(80);
      }
      const res = await axios.post(`${API}/ludo/sessions/${activeSession.id}/roll`);
      setGameState(res.data?.game_state || null);
      const finalDice = res.data?.dice_value;
      if (finalDice >= 1 && finalDice <= 6) {
        setDiceDisplay(finalDice);
      }
      setMovableTokens(
        Array.isArray(res.data?.movable_tokens)
          ? res.data.movable_tokens
          : (Array.isArray(res.data?.game_state?.movable_tokens) ? res.data.game_state.movable_tokens : [])
      );
      if (res.data?.triple_six_voided) {
        toast.info('Triple six: turn skipped');
      } else {
        toast.success(`Dice: ${res.data?.dice_value || '-'}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to roll dice');
    } finally {
      setDiceRolling(false);
      setLoading(false);
    }
  };

  const moveToken = async (tokenIndex) => {
    if (!activeSession?.id) return;
    try {
      setLoading(true);
      const res = await axios.post(`${API}/ludo/sessions/${activeSession.id}/move`, { token_index: tokenIndex });
      setGameState(res.data?.game_state || null);
      setMovableTokens([]);
      if (res.data?.status === 'finished') toast.success('Game finished');
      if (Array.isArray(res.data?.rewards) && res.data.rewards.length > 0) toast.success('Rewards distributed');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to move token');
    } finally {
      setLoading(false);
    }
  };

  const startLudoCall = async () => {
    if (!activeSession?.id) return;
    try {
      const res = await axios.post(`${API}/voip/start`, {
        scope: 'ludo',
        target_id: activeSession.id
      });
      setLudoCallInfo({
        id: res?.data?.call_id,
        host_id: user.id,
        participants: [user.id],
        status: 'active'
      });
      setLudoInCall(true);
      toast.success('Ludo group call started');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to start ludo call');
    }
  };

  const joinLudoCall = async () => {
    if (!ludoCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${ludoCallInfo.id}/join`);
      setLudoInCall(true);
      toast.success('Joined ludo group call');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to join ludo call');
    }
  };

  const leaveLudoCall = async () => {
    if (!ludoCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${ludoCallInfo.id}/leave`);
      setLudoInCall(false);
      setLudoMuted(false);
      toast.success('Left ludo group call');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to leave ludo call');
    }
  };

  const endLudoCall = async () => {
    if (!ludoCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${ludoCallInfo.id}/end`);
      const peers = (ludoCallInfo.participants || []).filter((pid) => pid && pid !== user.id);
      if (ws && ws.readyState === WebSocket.OPEN) {
        peers.forEach((peerId) => {
          ws.send(JSON.stringify({
            type: 'voip_call_end',
            call_id: ludoCallInfo.id,
            scope: 'ludo',
            target_id: activeSession?.id,
            recipient_id: peerId
          }));
        });
      }
      setLudoInCall(false);
      setLudoMuted(false);
      setLudoCallInfo(null);
      toast.success('Ludo group call ended');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to end ludo call');
    }
  };

  const boardCells = useMemo(() => {
    return Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, idx) => {
      const r = Math.floor(idx / BOARD_SIZE);
      const c = idx % BOARD_SIZE;
      const k = keyOf(r, c);
      const pathIndex = PATH.findIndex(([pr, pc]) => pr === r && pc === c);

      let zone = 'neutral';
      if (r < 6 && c < 6) zone = 'yard-green';
      if (r < 6 && c > 8) zone = 'yard-yellow';
      if (r > 8 && c < 6) zone = 'yard-red';
      if (r > 8 && c > 8) zone = 'yard-blue';
      if (r >= 6 && r <= 8 && c >= 6 && c <= 8) zone = 'center';

      const inGreenBase = r >= 1 && r <= 4 && c >= 1 && c <= 4;
      const inYellowBase = r >= 1 && r <= 4 && c >= 10 && c <= 13;
      const inRedBase = r >= 10 && r <= 13 && c >= 1 && c <= 4;
      const inBlueBase = r >= 10 && r <= 13 && c >= 10 && c <= 13;
      if (inGreenBase || inYellowBase || inRedBase || inBlueBase) zone = 'base';

      const homeGreen = HOME_LANE.green.some(([hr, hc]) => hr === r && hc === c);
      const homeYellow = HOME_LANE.yellow.some(([hr, hc]) => hr === r && hc === c);
      const homeRed = HOME_LANE.red.some(([hr, hc]) => hr === r && hc === c);
      const homeBlue = HOME_LANE.blue.some(([hr, hc]) => hr === r && hc === c);

      let lane = null;
      if (homeGreen) lane = 'green';
      if (homeYellow) lane = 'yellow';
      if (homeRed) lane = 'red';
      if (homeBlue) lane = 'blue';

      const isPath = pathIndex >= 0;
      const isSafe = isPath && SAFE_INDEXES.has(pathIndex);

      return { r, c, k, zone, isPath, isSafe, lane, pathIndex };
    });
  }, []);

  const playerCount = Math.max(1, activeSession?.players?.length || 0);
  const rawTurnIndex = Number(gameState?.turn_index || 0);
  const normalizedTurnIndex = ((rawTurnIndex % playerCount) + playerCount) % playerCount;
  const turnColor = activeSession?.players?.[normalizedTurnIndex]
    ? PLAYER_COLORS[normalizedTurnIndex % PLAYER_COLORS.length]
    : null;
  const isMyTurn = activeSession?.players?.[normalizedTurnIndex] === user?.id;
  const canRollDice = Boolean(
    activeSession?.status === 'playing' &&
    isMyTurn &&
    !loading &&
    !diceRolling
  );
  const turnPlayerId = activeSession?.players?.[normalizedTurnIndex];

  useEffect(() => {
    if (!activeSession?.id || activeSession?.status !== 'playing') return;
    const diceValue = Number(gameState?.dice_value);

    if (!Number.isInteger(diceValue) || diceValue < 1 || diceValue > 6) {
      lastRollNoticeKeyRef.current = '';
      return;
    }

    const noticeKey = `${activeSession.id}:${normalizedTurnIndex}:${diceValue}`;
    if (lastRollNoticeKeyRef.current === noticeKey) return;
    lastRollNoticeKeyRef.current = noticeKey;
    const rollerName = getPlayerLabel(turnPlayerId, normalizedTurnIndex);
    setLastRollInfo({ name: rollerName, value: diceValue });

    // Show transparency notifications for other players' dice rolls only.
    if (turnPlayerId && turnPlayerId !== user?.id) {
      toast.info(`${rollerName} rolled ${diceValue}`);
    }
  }, [
    activeSession?.id,
    activeSession?.status,
    gameState?.dice_value,
    normalizedTurnIndex,
    turnPlayerId,
    user?.id,
    getPlayerLabel
  ]);

  const { remoteStreams: ludoRemoteStreams, setMuted: setLudoVoipMuted } = useVoipMesh({
    ws,
    user,
    enabled: Boolean(ludoInCall && ludoCallInfo?.id),
    callId: ludoCallInfo?.id,
    scope: 'ludo',
    targetId: activeSession?.id,
    peerIds: ludoCallInfo?.participants || []
  });

  useEffect(() => {
    setLudoVoipMuted(ludoMuted);
  }, [ludoMuted, setLudoVoipMuted]);

  useEffect(() => {
    if (!ws) return;
    const onMessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'voip_call_end' && data.call_id && ludoCallInfo?.id === data.call_id) {
        setLudoInCall(false);
        setLudoMuted(false);
        setLudoCallInfo(null);
      }
    };
    ws.addEventListener('message', onMessage);
    return () => ws.removeEventListener('message', onMessage);
  }, [ws, ludoCallInfo?.id]);

  const highlightPathKeys = useMemo(() => {
    if (!turnColor) return new Set();
    const set = new Set(PATH.map(([r, c]) => keyOf(r, c)));
    HOME_LANE[turnColor].forEach(([r, c]) => set.add(keyOf(r, c)));
    return set;
  }, [turnColor]);

  const tokenRenderList = useMemo(() => {
    const players = activeSession?.players || [];
    const tokens = gameState?.tokens || {};
    const byCell = {};

    players.forEach((playerId, seatIndex) => {
      const color = PLAYER_COLORS[seatIndex] || 'red';
      const arr = Array.isArray(tokens[playerId]) ? tokens[playerId] : [-1, -1, -1, -1];

      arr.forEach((pos, tokenIndex) => {
        let coord = null;
        if (pos === -1) coord = BASE_SLOTS[color][tokenIndex] || BASE_SLOTS[color][0];
        else if (pos === 56) coord = [7, 7];
        else if (pos >= 52 && pos <= 55) coord = HOME_LANE[color][Math.max(0, Math.min(3, pos - 52))];
        else if (pos >= 0 && pos < 52) {
          const globalIdx = (OFFSETS[color] + pos) % 52;
          coord = PATH[globalIdx];
        }

        if (!coord) return;
        const k = keyOf(coord[0], coord[1]);
        if (!byCell[k]) byCell[k] = [];
        const isMine = playerId === user?.id;
        const isMovable = isMine && movableTokens.includes(tokenIndex);
        byCell[k].push({ playerId, seatIndex, color, tokenIndex, r: coord[0], c: coord[1], isMine, isMovable });
      });
    });

    const flat = [];
    Object.values(byCell).forEach((items) => {
      items.forEach((item, idx) => flat.push({ ...item, stackIndex: idx }));
    });

    return flat;
  }, [activeSession?.players, gameState?.tokens, movableTokens, user?.id]);

  const cellClass = (cell) => {
    if (cell.zone === 'center') return 'bg-white';
    if (cell.zone === 'yard-red') return 'bg-red-500';
    if (cell.zone === 'yard-green') return 'bg-green-500';
    if (cell.zone === 'yard-yellow') return 'bg-yellow-500';
    if (cell.zone === 'yard-blue') return 'bg-blue-500';
    if (cell.zone === 'base') return 'bg-white';
    if (cell.lane === 'red') return 'bg-red-300';
    if (cell.lane === 'green') return 'bg-green-300';
    if (cell.lane === 'yellow') return 'bg-yellow-300';
    if (cell.lane === 'blue') return 'bg-blue-300';
    if (cell.isPath) return 'bg-white';
    return 'bg-gray-100';
  };

  if (user?.role === 'guest') {
    return (
      <div className="p-4 space-y-3">
        <Card>
          <CardHeader>
            <CardTitle>Ludo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">Ludo is available for registered users only.</p>
            <p className="text-xs text-gray-500">Register your account to unlock game sessions and rewards.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 overflow-y-auto h-full pb-24">
      <style>{`
        @keyframes pinPulse { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-1px) } }
        @keyframes diceRoll {
          0% { transform: rotateX(0deg) rotateY(0deg) scale(1); }
          35% { transform: rotateX(180deg) rotateY(120deg) scale(1.08); }
          70% { transform: rotateX(320deg) rotateY(260deg) scale(1.05); }
          100% { transform: rotateX(360deg) rotateY(360deg) scale(1); }
        }
        .ludo-heavy-outline { box-shadow: inset 0 0 0 2px rgba(0,0,0,0.6); }
      `}</style>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Coin Wallet</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-2">
          <div>
            <div className="text-2xl font-bold text-blue-600">{wallet.coins || 0}</div>
            <div className="text-xs text-gray-500">Daily reward: {wallet.daily_reward || 100} coins</div>
          </div>
          <Button onClick={claimDaily} disabled={loading || !wallet.can_claim}>{wallet.can_claim ? 'Claim Reward' : 'Claimed'}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Ludo Match Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button variant={mode === 2 ? 'default' : 'outline'} onClick={() => setMode(2)}>2 Player</Button>
            <Button variant={mode === 4 ? 'default' : 'outline'} onClick={() => setMode(4)}>4 Player</Button>
            <Badge variant="secondary">Entry: 100</Badge>
          </div>
          <Button onClick={createSession} disabled={loading || Boolean(activeSession)}>Create Match</Button>
          <div className="flex gap-2">
            <Input placeholder="Invite token" value={joinToken} onChange={(e) => setJoinToken(e.target.value)} />
            <Button variant="outline" onClick={() => joinByToken()} disabled={loading || Boolean(activeSession)}>Join</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Live Sessions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {sessions.length === 0 && <p className="text-sm text-gray-500">No active sessions found.</p>}
          {sessions.map((session) => (
            <div key={session.id} className="flex items-center justify-between border rounded-md p-2">
              <div>
                <div className="text-sm font-medium">{session.mode}P Session</div>
                <div className="text-xs text-gray-500">{session.players?.length || 0}/{session.max_players} joined</div>
              </div>
              <div className="flex items-center gap-2">
                {Array.isArray(session.players) && session.players.includes(user?.id) ? (
                  <Badge className="bg-green-600">Joined</Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => (session.invite_token ? joinByToken(session.invite_token) : joinBySessionId(session.id))}
                    disabled={loading || (session.players?.length || 0) >= session.max_players || (activeSession && activeSession.id !== session.id)}
                  >
                    {activeSession && activeSession.id !== session.id ? 'In Game' : 'Join'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {activeSession && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Current Match</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{activeSession.mode}P - {activeSession.players?.length || 0}/{activeSession.max_players}</div>
              <Badge>{activeSession.status}</Badge>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: activeSession.max_players || 2 }).map((_, i) => {
                const pid = activeSession.players?.[i];
                const color = PLAYER_COLORS[i] || 'red';
                return (
                  <div key={i} className="flex items-center gap-2 rounded border p-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLOR_HEX[color] }} />
                    <span className={`text-xs ${pid === user?.id ? 'font-semibold text-blue-700' : 'text-gray-600'}`}>
                      {getPlayerLabel(pid, i)}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              {activeSession.host_id === user?.id && activeSession.status !== 'playing' && (
                <Button onClick={() => startSession(activeSession.id)} disabled={loading || (activeSession.players?.length || 0) !== activeSession.max_players}>Start Game</Button>
              )}
              {activeSession.host_id === user?.id && activeSession.status !== 'playing' && (
                <Button variant="outline" onClick={() => cancelSession(activeSession.id)} disabled={loading}>Cancel</Button>
              )}
              {activeSession.host_id === user?.id && activeSession.status === 'playing' && !ludoCallInfo && (
                <Button variant="outline" onClick={startLudoCall} disabled={loading} title="Start ludo group call">
                  <Phone className="w-4 h-4 mr-2" /> Call
                </Button>
              )}
              <Button variant="outline" onClick={() => leaveSession(activeSession.id)} disabled={loading}>Leave</Button>
            </div>

            {ludoCallInfo && (
              <div className="rounded-md border px-3 py-2 bg-blue-50 flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${ludoInCall ? 'text-green-600' : 'text-blue-600'}`}>
                    {ludoInCall ? 'In ludo call' : 'Ludo call active'}
                  </span>
                  <span className="text-gray-600">
                    {(ludoCallInfo.participants || []).length} participant{(ludoCallInfo.participants || []).length === 1 ? '' : 's'}
                  </span>
                  <span className={`font-medium ${ludoMuted ? 'text-orange-600' : 'text-gray-600'}`}>
                    {ludoMuted ? 'Muted' : 'Unmuted'}
                  </span>
                  {Object.entries(ludoRemoteStreams).map(([peerId, stream]) => (
                    <audio
                      key={peerId}
                      autoPlay
                      playsInline
                      ref={(el) => {
                        if (!el) return;
                        if (el.srcObject !== stream) el.srcObject = stream;
                      }}
                      style={{ display: 'none' }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  {ludoInCall && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setLudoMuted((v) => !v)}>
                      {ludoMuted ? <MicOff className="w-3.5 h-3.5 mr-1" /> : <Mic className="w-3.5 h-3.5 mr-1" />}
                      {ludoMuted ? 'Unmute' : 'Mute'}
                    </Button>
                  )}
                  {ludoInCall && ludoCallInfo?.host_id === user.id && (
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-red-600" onClick={endLudoCall}>
                      <PhoneOff className="w-3.5 h-3.5 mr-1" /> End
                    </Button>
                  )}
                  <Button
                    variant={ludoInCall ? 'outline' : 'default'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={ludoInCall ? leaveLudoCall : joinLudoCall}
                  >
                    {ludoInCall ? 'Leave' : 'Join'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Ludo Board</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mx-auto w-full max-w-[560px] aspect-square rounded-2xl border-4 border-gray-800 bg-white p-2 shadow-inner">
            <div className="relative h-full w-full rounded-xl overflow-hidden">
              <div
                className="grid h-full w-full gap-[1px] bg-gray-400 rounded-xl ludo-heavy-outline"
                style={{ gridTemplateColumns: `repeat(${BOARD_SIZE}, minmax(0, 1fr))` }}
              >
                {boardCells.map((cell) => {
                  const isTurnPath = turnColor && highlightPathKeys.has(cell.k) && (cell.isPath || cell.lane === turnColor);
                  const startColor = Object.entries(START_INDEX_BY_COLOR).find(([, idx]) => idx === cell.pathIndex)?.[0];
                  return (
                    <div
                      key={cell.k}
                      className={`relative border border-gray-300 ${cellClass(cell)} ${isTurnPath ? 'ring-1 ring-inset animate-pulse' : ''}`}
                      style={isTurnPath ? { boxShadow: `inset 0 0 0 1px ${COLOR_HEX[turnColor]}` } : undefined}
                    >
                      {startColor && (
                        <span
                          className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white shadow"
                          style={{ backgroundColor: COLOR_HEX[startColor] }}
                        />
                      )}
                      {cell.isSafe && (
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] sm:text-xs font-bold text-gray-700">★</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pixel-polish overlays for classic board look */}
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-0 top-0 h-[40%] w-[40%] border-2 border-black/70" />
                <div className="absolute right-0 top-0 h-[40%] w-[40%] border-2 border-black/70" />
                <div className="absolute left-0 bottom-0 h-[40%] w-[40%] border-2 border-black/70" />
                <div className="absolute right-0 bottom-0 h-[40%] w-[40%] border-2 border-black/70" />

                <div className="absolute left-[6.666%] top-[6.666%] h-[26.666%] w-[26.666%] border-2 border-black/60 bg-white/85" />
                <div className="absolute right-[6.666%] top-[6.666%] h-[26.666%] w-[26.666%] border-2 border-black/60 bg-white/85" />
                <div className="absolute left-[6.666%] bottom-[6.666%] h-[26.666%] w-[26.666%] border-2 border-black/60 bg-white/85" />
                <div className="absolute right-[6.666%] bottom-[6.666%] h-[26.666%] w-[26.666%] border-2 border-black/60 bg-white/85" />

                {/* Center home triangles: one per player color */}
                <div className="absolute left-[40%] top-[40%] h-[20%] w-[20%]">
                  <div
                    className="absolute inset-0"
                    style={{
                      clipPath: 'polygon(50% 50%, 0% 0%, 100% 0%)',
                      background: COLOR_HEX.yellow
                    }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      clipPath: 'polygon(50% 50%, 100% 0%, 100% 100%)',
                      background: COLOR_HEX.blue
                    }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      clipPath: 'polygon(50% 50%, 0% 100%, 100% 100%)',
                      background: COLOR_HEX.red
                    }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      clipPath: 'polygon(50% 50%, 0% 0%, 0% 100%)',
                      background: COLOR_HEX.green
                    }}
                  />
                  <div className="absolute inset-0 border-2 border-black/70" />
                </div>

                {/* Final home-entry direction arrows into each triangle */}
                <div
                  className="absolute -translate-x-1/2 -translate-y-1/2 text-[13px] font-black"
                  style={{ left: '36.666%', top: '50%', color: COLOR_HEX.green, textShadow: '0 0 2px #fff' }}
                >
                  →
                </div>
                <div
                  className="absolute -translate-x-1/2 -translate-y-1/2 text-[13px] font-black"
                  style={{ left: '50%', top: '36.666%', color: COLOR_HEX.yellow, textShadow: '0 0 2px #fff' }}
                >
                  ↓
                </div>
                <div
                  className="absolute -translate-x-1/2 -translate-y-1/2 text-[13px] font-black"
                  style={{ left: '63.333%', top: '50%', color: COLOR_HEX.blue, textShadow: '0 0 2px #fff' }}
                >
                  ←
                </div>
                <div
                  className="absolute -translate-x-1/2 -translate-y-1/2 text-[13px] font-black"
                  style={{ left: '50%', top: '63.333%', color: COLOR_HEX.red, textShadow: '0 0 2px #fff' }}
                >
                  ↑
                </div>
              </div>


                {houseLabels.map((house) => {
                  if (!house.playerId) return null;
                  let left = '20%';
                  let top = '20%';
                  if (house.color === 'yellow') {
                    left = '80%';
                    top = '20%';
                  } else if (house.color === 'red') {
                    left = '20%';
                    top = '80%';
                  } else if (house.color === 'blue') {
                    left = '80%';
                    top = '80%';
                  }
                  return (
                    <div
                      key={`${house.color}-${house.playerId}`}
                      className="absolute -translate-x-1/2 -translate-y-1/2 max-w-[28%] truncate px-1 text-center text-[10px] sm:text-xs font-extrabold"
                      style={{
                        left,
                        top,
                        color: COLOR_HEX[house.color],
                        textShadow: '0 1px 2px rgba(255,255,255,0.95)'
                      }}
                      title={house.label}
                    >
                      {house.label}
                    </div>
                  );
                })}

              <div className="absolute inset-0 pointer-events-none">
                {tokenRenderList.map((token) => {
                  const [dx, dy] = getSafeTokenOffset(token, token.stackIndex);
                  const left = ((token.c + 0.5) * 100) / BOARD_SIZE;
                  const top = ((token.r + 0.5) * 100) / BOARD_SIZE;
                  const isTurnOwner = activeSession?.players?.[normalizedTurnIndex] === token.playerId;
                  const clickable =
                    token.isMovable &&
                    activeSession?.players?.[normalizedTurnIndex] === user?.id &&
                    gameState?.dice_value !== null;
                  return (
                    <div
                      key={`${token.playerId}-${token.tokenIndex}`}
                      className={`absolute transition-all duration-500 ease-in-out ${clickable ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
                      style={{
                        left: `calc(${left}% + ${dx}px)`,
                        top: `calc(${top}% + ${dy}px)`,
                        transform: 'translate(-50%, -50%)'
                      }}
                      onClick={() => clickable && moveToken(token.tokenIndex)}
                      title={clickable ? `Move token ${token.tokenIndex + 1}` : undefined}
                    >
                      <div className={clickable ? 'drop-shadow-[0_0_6px_rgba(255,255,255,0.95)]' : ''}>
                        <PinToken color={token.color} isActive={isTurnOwner || clickable} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mt-2 text-xs sm:text-sm text-gray-700">
            Last roll: <span className="font-semibold">{lastRollInfo?.name || '-'}</span> -&gt; <span className="font-semibold">{lastRollInfo?.value ?? '-'}</span>
          </div>
        </CardContent>
      </Card>

      {activeSession?.status === 'playing' && (
        <div className="mt-2">
          <Card className="border-blue-200 shadow-md">
            <CardContent className="py-3 flex items-center justify-between gap-3 bg-white">
              <div className="text-xs text-gray-700">
                <div className="font-semibold">
                  Turn: {isMyTurn ? 'You' : 'Other player'}
                </div>
                <div className="text-gray-500">
                  {gameState?.dice_value === null ? 'Tap dice to roll' : 'Tap highlighted token to move'}
                </div>
              </div>
              <button
                type="button"
                onClick={rollDice}
                disabled={!canRollDice}
                className="rounded-lg transition hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Roll dice"
              >
                <DiceFace value={diceDisplay} rolling={diceRolling} />
              </button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LudoTab;
